/**
 * Choose the design techniques for one planned graphic. Fit is a judgement about content, so a
 * model makes it.
 *
 * Earlier scenes are passed with their copy so repetition can be judged rather than merely
 * avoided — successive list items used as back-to-back titles should look alike, the same technique
 * on unrelated scenes should not.
 *
 * The call runs on the conversation's own model. A second model would put another provider, another
 * rate-limit bucket and another price on the critical path of every scene, to rank one-line
 * summaries against a short brief.
 *
 * It runs as a child of the plan's tool call, so what it chose and why is saved with that call and
 * what it costs is counted with the run.
 */
import type { TechniquePolicy } from './recipeTechniquePolicies';
import { maxPicksFor } from './recipeTechniquePolicies';
import type { TechniqueCandidate } from './suggestTechniques';
import type { Conversation } from 'js/src/agent/Conversation';
import type { ScenePlan } from 'js/src/data/html-design';
import { ErrorReporter } from 'js/src/libs/error-reporter';

export const CHOOSER_TASK = 'html_design_technique_choice';

export interface EarlierScene {
    text: string[];
    skills: string[];
}

/** The video skill the agent loaded to build this video. */
export interface VideoRecipe {
    name: string;
    instructions: string;
}

export interface ChosenTechnique {
    skill: string;
    /** Becomes the assignment's `for`, so it is read by whoever authors the scene. */
    why: string;
    /** The phase the technique lands on, 1-based. Absent when it governs the whole graphic. */
    atPhase?: number;
}

const HOW_MANY_DEFAULT = `Pick ONE technique, or TWO when the frame genuinely
needs a second doing a different job. Never more than two.`;

function howManyPerPhase(max: number): string {
    return `This video assigns techniques beat by beat. Pick ONE technique for each phase whose
arrival or change performs something, each with that phase's "atPhase", and no more than ${max} in
all. Two picks never share a phase. A phase that only carries the scene's ordinary entrance gets
no pick of its own: if the scene has a baseline entrance every beat uses, pick it once and leave
its "atPhase" out.`;
}

function systemPrompt(policy?: TechniquePolicy): string {
    const howMany = policy?.perPhase ? howManyPerPhase(policy.maxPerGraphic) : HOW_MANY_DEFAULT;
    const recipeCap = policy?.perPhase
        ? ''
        : ` Where it asks for more techniques per scene than two, pick the two it would put
most weight on.`;
    return `You choose the motion-design techniques for one scene of a motion graphics video.

You are given the scene's copy and layout, the library of techniques available, and every earlier
scene in this video with the techniques it used. ${howMany}

You may also be given the recipe the video is being built under: the brief for the whole format.
When there is one, it governs. If it names the techniques its scenes are built from, or the kind
of moment each is for, pick from those and follow its rules for choosing between them, over rules
3 and 4 below.${recipeCap} Picks still come from the library, and rules 1 and 2 still hold.

How to choose:

1. FIT FIRST. The technique has to make sense of what this scene actually says. A strike-through
   belongs on a correction or a replacement, not on two cities being compared. A typewriter belongs
   on something being composed or queried. A count-up belongs on a quantity, not on a year. A
   drawn arrow or annotation does not belong on any rise or fall: the change is performed by what
   changed — a counter or wheel for a figure, the chart for a series. If a technique would be
   arbitrary against this copy, it is the wrong pick however interesting it is. A geographic map is
   built by a map technique: when the frame is a map, one pick is the map technique for what the
   map does — finding one place, shading figures by place, or routes between places — and the
   other, if there is one, carries the copy.

2. THE COPY HAS TO ALREADY HOLD WHAT THE TECHNIQUE NEEDS. A candidate marked "needs" only works on
   a scene whose copy or figures already contain that. A strike-and-replace needs the word being
   replaced and its replacement; a scene holding neither is not a scene for it. Picking one anyway
   does not get you the technique — the assignment is binding, so what it gets you is copy invented
   to host it: a phrase nobody wrote and the narration never says. If this scene's copy does not
   meet a candidate's "needs", it is not a candidate, however well it suits the subject.

3. PREFER THE MORE INTERESTING OPTION. Where two techniques both fit, take the one with more in it.
   Each candidate is marked "adventurous": true or false — false means a plain entrance, type that
   rises or fades or slides. Those are not wrong, but they are what gets chosen by default and a
   video made entirely of them looks unmade. Choose a plain one when the frame genuinely wants
   restraint — a sombre line, a legal note, a quiet close — and say so in your reason.

4. VARIETY ACROSS THE VIDEO, BUT REPETITION WHERE IT IS EARNED. A candidate marked
   "usedAlready": true was assigned to an earlier scene. Picking one again is correct when this
   scene is the same *kind* of moment as that one — successive items of one list used as
   back-to-back scene titles, the two halves of a comparison, a recurring section header. Those
   should look like each other. Otherwise take something the video has not used: repeating a
   technique across unrelated scenes is a tic, and the viewer reads it as one effect applied to a
   video rather than as each moment being given what it needs. A scene marked "Reproduces" recreates
   a figure from the user's material: pick what draws that figure's form, used already or not.

5. PLACE IT ON A PHASE. The scene's phases are numbered below. Give each pick the "atPhase" of the
   one moment it lands on — the phase whose arrival or rearrangement the technique actually performs.
   A technique tied to a phase is something that happens at a moment; one left unplaced becomes a
   property of the whole graphic, and the frame fills up with elements built to carry it. Omit
   "atPhase" only for a technique that genuinely governs the scene throughout, such as an ambient
   treatment or a pacing staple.

Answer with JSON only, no prose around it:
{"picks": [{"skill": "<exact name from the library>", "why": "<one clause: what it does for this frame>", "atPhase": <phase number>}]}

Every "skill" must be copied exactly from the library below. Do not invent names. The "why" is shown
to whoever builds the scene, so make it about this frame rather than about the technique.`;
}

function userPrompt(
    frame: ScenePlan,
    candidates: TechniqueCandidate[],
    earlier: EarlierScene[],
    recipe?: VideoRecipe,
    policy?: TechniquePolicy,
): string {
    const lines: string[] = recipe
        ? [`## The recipe: ${recipe.name}`, '', recipe.instructions.trim(), '']
        : [];
    lines.push('## This scene', '');
    lines.push(`Copy on screen: ${frame.text.length ? JSON.stringify(frame.text) : 'none'}`);
    lines.push(
        `Graphics planned: ${frame.graphics.length ? JSON.stringify(frame.graphics) : 'none'}`,
    );
    lines.push(`Layout: ${frame.layout || 'unspecified'}`);
    // How many times the graphic advances, and what carries each advance, separates a technique
    // that plays once on arrival from one that hands a frame from each phase to the next.
    // Numbered, because a pick refers to a phase by its position and the only other number on the
    // line is the time — an unnumbered list invites `atPhase: 3` meaning "the 3s one".
    lines.push('Phases:');
    frame.phases.forEach(({ at, enters, transforms, exits }, index) => {
        lines.push(
            `  ${index + 1}. ${[
                `${at}s`,
                enters ?? 'nothing new',
                transforms ? `(${transforms})` : '',
                exits ? `[exit: ${exits}]` : '',
            ]
                .filter(Boolean)
                .join(' ')}`,
        );
    });
    // Whether the scene plots anything, and how many figures, is the strongest signal between a
    // chart technique, a single counter and a proportion drawn as a metaphor.
    const series = (frame.data ?? []).map(
        ({ label, values }) => `${label} (${values.length}: ${values.join(', ')})`,
    );
    lines.push(`Figures plotted: ${series.length ? series.join('; ') : 'none'}`);
    if (frame.reproduces) {
        lines.push(`Reproduces: ${frame.reproduces}`);
    }
    lines.push('');

    if (earlier.length) {
        lines.push('## Earlier scenes in this video', '');
        earlier.forEach((scene, index) => {
            lines.push(
                `${index + 1}. copy ${JSON.stringify(scene.text)} — used ${
                    scene.skills.length ? scene.skills.join(', ') : 'nothing'
                }`,
            );
        });
        lines.push('');
    } else {
        lines.push('## Earlier scenes in this video', '', 'None — this is the first.', '');
    }

    lines.push('## The library', '');
    for (const { skill, summary, adventurous, usedAlready, requiredFromPlan } of candidates) {
        lines.push(
            `- ${skill} (adventurous: ${adventurous}, usedAlready: ${usedAlready}) — ${summary}` +
                (requiredFromPlan ? ` — needs ${requiredFromPlan}` : ''),
        );
    }
    lines.push(
        '',
        policy?.perPhase
            ? 'Return one pick for each phase that performs something.'
            : 'Return one or two picks.',
    );
    return lines.join('\n');
}

/** A phase the plan does not have would point the author at nothing, so it is dropped rather
 *  than clamped — an unplaced technique is the existing, harmless behaviour. */
function phaseOf(pick: unknown, phaseCount: number): number | undefined {
    const atPhase = (pick as { atPhase?: unknown })?.atPhase;
    if (typeof atPhase !== 'number' || !Number.isInteger(atPhase)) {
        return undefined;
    }
    return atPhase >= 1 && atPhase <= phaseCount ? atPhase : undefined;
}

/** Nothing survives that was not on offer, so the caller can trust every name it gets back. */
function validate(
    raw: unknown,
    candidates: TechniqueCandidate[],
    phaseCount: number,
    policy?: TechniquePolicy,
): ChosenTechnique[] {
    const picks = (raw as { picks?: unknown })?.picks;
    if (!Array.isArray(picks)) {
        return [];
    }
    const available = new Set(candidates.map(({ skill }) => skill));
    const chosen: ChosenTechnique[] = [];
    for (const pick of picks) {
        const skill = (pick as { skill?: unknown })?.skill;
        const why = (pick as { why?: unknown })?.why;
        if (typeof skill !== 'string' || !available.has(skill)) {
            continue;
        }
        if (chosen.some((already) => already.skill === skill)) {
            continue;
        }
        const atPhase = phaseOf(pick, phaseCount);
        if (
            policy?.perPhase &&
            atPhase !== undefined &&
            chosen.some((already) => already.atPhase === atPhase)
        ) {
            continue;
        }
        chosen.push({
            skill,
            why: typeof why === 'string' && why.trim() ? why.trim() : 'chosen for this frame',
            ...(atPhase === undefined ? {} : { atPhase }),
        });
    }
    return chosen.slice(0, maxPicksFor(policy));
}

function parseJson(content: string): unknown {
    const match = content.match(/\{[\s\S]*\}/);
    if (!match) {
        return null;
    }
    try {
        return JSON.parse(match[0]);
    } catch {
        return null;
    }
}

/**
 * Never throws: an empty result means the caller should fall back, as it also does when
 * `startChoice` has no conversation to open.
 *
 * A child conversation cannot ask for a JSON response format, so the answer is read as the first
 * `{...}` in the reply, which the prompt's "JSON only" instruction keeps close to the whole of it.
 */
export async function chooseTechniques(
    frame: ScenePlan,
    candidates: TechniqueCandidate[],
    earlier: EarlierScene[],
    startChoice: (systemMessage: string) => Conversation | null,
    parentActionGroup?: string,
    recipe?: VideoRecipe,
    policy?: TechniquePolicy,
): Promise<ChosenTechnique[]> {
    if (candidates.length === 0) {
        return [];
    }
    const choice = startChoice(systemPrompt(policy));
    if (!choice) {
        return [];
    }
    try {
        await choice.addUserMessage(userPrompt(frame, candidates, earlier, recipe, policy));
        const result = await choice.executeWithLLM({ parentActionGroup });
        return result.success && result.message
            ? validate(parseJson(result.message), candidates, frame.phases.length, policy)
            : [];
    } catch (error) {
        // A cancelled build and a rate limit are both expected; nothing here stalls a build.
        const message = error instanceof Error ? error.message : String(error);
        const expected =
            (error as { name?: string })?.name === 'AbortError' ||
            /Rate limit exceeded|Daily usage limit reached|Request aborted/.test(message);
        if (!expected) {
            ErrorReporter.capture(error instanceof Error ? error : new Error(message), {
                extra: { context: CHOOSER_TASK },
            });
        }
        return [];
    }
}
